import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { Info, Loader2 } from 'lucide-react';
import { ErrorState } from '../components/ui/ErrorState';
import { NotFound } from '../pages/NotFound/NotFound';
import { selectHasWmsAccess } from '../features/auth/authSelectors';
import { fetchScopedWarehouses } from '../features/warehouseContext/warehouseContextThunks';
import { selectCurrentWarehouse } from '../features/warehouseContext/warehouseContextSlice';
import { warehouseIsIdle } from '../utils/inventoryLabels';

/** Why the module looks empty, when the reason is the warehouse and not the screen. */
const noteFor = (warehouses, current) => {
  if (warehouses.length === 0) return 'Tu cuenta todavía no tiene bodegas a su alcance.';
  if (current && warehouseIsIdle(current)) {
    return `${current.name} no tiene clientes con contrato activo: puedes mirar lo que quedó adentro, pero no operar.`;
  }
  return null;
};

/**
 * The frame of the warehouse module, mounted by a workspace around its
 * `/warehouse/*` routes.
 *
 * It answers the two things every screen inside would otherwise have to ask on
 * its own: whether the account has the module, and which warehouses it reaches.
 * The warehouse in use is chosen in the top bar (`WarehouseSwitcher`, mounted by
 * `Header`), which reads what this loads.
 *
 * Without the module the address does not exist — the same screen any other
 * wrong address gets — because the menu entry is not drawn either, and hiding
 * only the link would leave the section reachable by typing it.
 *
 * The screens wait for the first answer on purpose: the remembered warehouse
 * may no longer be in reach, and a screen that asked for its data before that
 * was known would ask with a warehouse the server is about to refuse.
 */
export const WarehouseScope = () => {
  const dispatch = useDispatch();
  const hasAccess = useSelector(selectHasWmsAccess);
  const { warehouses, loaded, error } = useSelector((state) => state.warehouseContext);
  const current = useSelector(selectCurrentWarehouse);

  const loadWarehouses = useCallback(() => dispatch(fetchScopedWarehouses()), [dispatch]);

  useEffect(() => {
    if (hasAccess) loadWarehouses();
  }, [hasAccess, loadWarehouses]);

  if (!hasAccess) return <NotFound />;

  if (!loaded) {
    return error ? (
      <ErrorState
        title="No se pudieron cargar tus bodegas"
        description={error}
        onRetry={loadWarehouses}
      />
    ) : (
      <div data-testid="warehouse-scope-loading" className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-ninja-green animate-spin" />
      </div>
    );
  }

  const note = noteFor(warehouses, current);

  return (
    <div data-testid="warehouse-scope" className="space-y-6">
      {note && (
        <p
          data-testid="warehouse-scope-note"
          className="flex items-center gap-2 text-sm text-text-secondary"
        >
          <Info size={16} className="text-warning flex-shrink-0" />
          {note}
        </p>
      )}
      <Outlet />
    </div>
  );
};
